import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storesAPI, brandingAPI } from '../lib/api';
import { storage, applyBranding, resetBranding } from '../lib/utils';
import { useAuth } from './AuthContext';

const StoreContext = createContext(null);

export function StoreProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load stores when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadStores();
    } else {
      setStores([]);
      setCurrentStore(null);
      setBranding(null);
      resetBranding();
    }
  }, [isAuthenticated]);

  // Load branding when store changes
  useEffect(() => {
    if (currentStore) {
      loadBranding(currentStore.id);
      storage.set('current_store', currentStore);
    } else {
      setBranding(null);
      resetBranding();
      storage.remove('current_store');
    }
  }, [currentStore]);

  // Restore last store on mount
  useEffect(() => {
    const savedStore = storage.get('current_store');
    if (savedStore && isAuthenticated) {
      setCurrentStore(savedStore);
    }
  }, [isAuthenticated]);

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await storesAPI.list();
      setStores(response.data);
      
      // Auto-select store for staff
      if (user?.store_id && !currentStore) {
        const userStore = response.data.find(s => s.id === user.store_id);
        if (userStore) {
          setCurrentStore(userStore);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading stores');
    } finally {
      setLoading(false);
    }
  }, [user, currentStore]);

  const loadBranding = useCallback(async (storeId) => {
    try {
      const response = await brandingAPI.get(storeId);
      setBranding(response.data);
      applyBranding(response.data);
    } catch (err) {
      console.error('Error loading branding:', err);
    }
  }, []);

  const selectStore = useCallback((store) => {
    setCurrentStore(store);
  }, []);

  const clearStore = useCallback(() => {
    setCurrentStore(null);
    setBranding(null);
    resetBranding();
    storage.remove('current_store');
  }, []);

  const updateBranding = useCallback(async (storeId, data) => {
    try {
      const response = await brandingAPI.update(storeId, data);
      setBranding(response.data);
      applyBranding(response.data);
      return response.data;
    } catch (err) {
      throw err;
    }
  }, []);

  const refreshStores = useCallback(() => {
    return loadStores();
  }, [loadStores]);

  const value = {
    stores,
    currentStore,
    branding,
    loading,
    error,
    hasStore: !!currentStore,
    selectStore,
    clearStore,
    loadBranding,
    updateBranding,
    refreshStores,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}

export default StoreContext;
